import {default as cx} from "classnames"
import {Calendar} from "primereact/calendar"
import {Dropdown} from "primereact/dropdown"
import {MultiSelect} from "primereact/multiselect"
import {Skeleton} from "primereact/skeleton"
import {useContext, useEffect, useState, useRef} from "react"
import {useBlocker} from "react-router-dom"
import {Button, Form, Modal, Spinner} from "react-bootstrap"
import useSWR from "swr"

import {OrienteeOrientations} from "./OrienteeOrientations.jsx"
import {wrappedPut} from "../utilities/AxiosHelper.jsx"
import {buildFacilityOptions, fetcherGet, getJSDate} from "../utilities/general"
import RefContext from "../utilities/RefContext"

function OrienteeDetails({orientee, setOrientee, isOrienteeLoading, editable, headerAction}) {
    const programs = ["Associate's degree program", "Bachelor's degree program", "Master's degree program", "Unsure"]

    const {data: experienceLevels} = useSWR("/experience-levels", fetcherGet, {
        fallbackData: [],
    })
    const {data: facilities, isLoading: isFacilitiesLoading} = useSWR(
        orientee.organization_id ? `/organization/${orientee.organization_id}/facility` : null,
        fetcherGet,
        {fallbackData: []}
    )
    const {data: units, isLoading: isUnitsLoading} = useSWR(
        orientee.organization_id && orientee.facility_id
            ? `/organization/${orientee.organization_id}/facility/${orientee.facility_id}/unit`
            : null,
        fetcherGet,
        {fallbackData: []}
    )
    const {data: positions, isLoading: isPositionsLoading} = useSWR(
        orientee.organization_id ? `/organization/${orientee.organization_id}/position-list` : null,
        fetcherGet,
        {fallbackData: []}
    )
    const {data: preceptors, isLoading: isPreceptorsLoading} = useSWR(
        `/preceptors?unit-id=${orientee.unit_id}`,
        fetcherGet,
        {
            fallbackData: [],
        }
    )

    const [errorHireDate, setErrorHireDate] = useState("")
    const [errorFacility, setErrorFacility] = useState("")
    const [errorUnit, setErrorUnit] = useState("")
    const [errorStandardLength, setErrorStandardLength] = useState("")
    const [saving, setSaving] = useState(false)
    const {dispatch} = useContext(RefContext)

    const [isOrienteeEdited, setIsOrienteeEdited] = useState(false)

    const changeValue = (key, value) => {
        setOrientee((prev) => ({...prev, [key]: value}))
        setIsOrienteeEdited(true)
    }

    const bypassRef = useRef(false)
    const blocker = useBlocker(() => isOrienteeEdited && !bypassRef.current)

    const handleSaveAndClose = () => {
        saveOrientee().then((ok) => (ok ? blocker.proceed() : blocker.reset()))
    }

    useEffect(() => {
        calculateStartDate()
    }, [orientee.hire_date])

    const saveOrientee = (event) => {
        event?.preventDefault()
        let hasError = false
        setErrorHireDate("")
        setErrorFacility("")
        setErrorUnit("")
        setErrorStandardLength("")
        if (!orientee.hire_date) {
            setErrorHireDate("Hire date is required")
            hasError = true
        }
        if (!orientee.facility_id) {
            setErrorFacility("Facility is required")
            hasError = true
        }
        if (!orientee.unit_id) {
            setErrorUnit("Unit is required")
            hasError = true
        }
        if (!orientee.standard_orientation_length_weeks) {
            setErrorStandardLength("Standard orientation length is required")
            hasError = true
        }
        if (hasError) {
            return Promise.resolve(false)
        }
        setSaving(true)

        return wrappedPut(`/orientees/${orientee.id}`, orientee)
            .then((response) => {
                dispatch("showToast", {show: true, variant: "success", text: response.data.Message, timeout: 5000})
                setIsOrienteeEdited(false)
                return true
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.["Error"]
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: errorMessage || "Error saving orientation details",
                    timeout: 5000,
                })
                return false
            })
            .finally(() => {
                setSaving(false)
            })
    }

    const calculateStartDate = () => {
        if (orientee.hire_date && orientee.unit_id) {
            const unit = units.find((unit) => unit.id === orientee.unit_id)
            if (unit) {
                let startDate = new Date(orientee.hire_date)
                if (startDate) {
                    startDate.setDate((startDate.getDate() + unit.num_onboarding_weeks * 7) - startDate.getDay())
                    changeValue(
                        "start_date",
                        `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(
                            2,
                            "0"
                        )}-${String(startDate.getDate()).padStart(2, "0")}`
                    )
                } else {
                    changeValue("start_date", null)
                }
            }
        } else {
            changeValue("start_date", null)
        }
    }

    // this allows the preceptors that are not assigned to the unit this orientation is in to be hidden
    // if not they will show as nulls because they do not get looked up
    let validPreceptorIds
    if (preceptors && orientee.preceptors) {
        validPreceptorIds = new Set(preceptors.map((p) => p.id))
    }

    return (
        <>
            <div className="container-fluid d-flex flex-column">
                {blocker.state === "blocked" && (
                    <Modal show centered onHide={() => blocker.reset()} animation={false}>
                        <Modal.Header closeButton>
                            <Modal.Title>Save your changes?</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>You have unsaved changes on this page.</Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => blocker.proceed()}>
                                Do Not Save
                            </Button>
                            <Button variant="primary" onClick={handleSaveAndClose}>
                                Save and Close
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}
                <div className="card p-8 mt-6">
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 mb-4">
                        <h1 className="mb-0">Details</h1>
                        {headerAction}
                    </div>
                    <div className="d-flex flex-column flex-sm-row gap-1 gap-sm-4 mb-4">
                        <div className="form-label">Employee ID</div>
                        <span className="text-break">{orientee.employee_id}</span>
                    </div>
                    <div className="d-flex flex-column flex-sm-row gap-1 gap-sm-4 mb-4">
                        <div className="form-label">Orientation Status</div>
                        <span className="text-break">{orientee.orientation_status}</span>
                    </div>
                    <div className="d-flex flex-column flex-sm-row gap-1 gap-sm-4 mb-4">
                        <div className="form-label">Current Week</div>
                        <span className="text-break">{orientee.current_week_of_hire}</span>
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-4 mb-4">
                        <div className="col-lg-6 mb-4">
                            <div className="form-label required">Orientation Hire Date</div>
                            {isOrienteeLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <Calendar
                                    showIcon
                                    className="w-100 p-dropdown-tall"
                                    placeholder="Hire Date"
                                    value={getJSDate(orientee.hire_date)}
                                    onChange={(e) => changeValue("hire_date", e.value)}
                                    disabled={!editable}
                                />
                            )}
                            <div className="form-text text-gray-700">
                                <span className="fw-bold">Includes</span> hospital onboarding week
                            </div>
                            <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
                                {errorHireDate}
                            </div>
                        </div>
                        <div className="col-lg-6 mb-4">
                            <div className="form-label required">Anticipated Orientation Length</div>
                            {isOrienteeLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <Form.Control
                                    name="standard_orientation_length_weeks"
                                    type="number"
                                    required
                                    value={orientee.standard_orientation_length_weeks || ""}
                                    onChange={(e) => changeValue("standard_orientation_length_weeks", e.target.value)}
                                    disabled={!editable}
                                />
                            )}
                            <div className="form-text text-gray-700">
                                Orientation length in <span className="fw-bold">weeks</span>
                            </div>
                            <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
                                {errorStandardLength}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex flex-column flex-sm-row gap-1 gap-sm-4 mb-4">
                        <div className="col-lg-6 mb-4">
                            <div className="form-label required">Start Date</div>
                            {isOrienteeLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <Calendar
                                    showIcon
                                    className="w-100 p-dropdown-tall"
                                    placeholder="Start Date"
                                    value={getJSDate(orientee.start_date)}
                                    onChange={(e) => changeValue("start_date", e.value)}
                                    disabled={!editable}
                                    disabledDays={[1, 2, 3, 4, 5, 6]}
                                />
                            )}
                        </div>
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-4 mb-4">
                        <div className="col-lg-6 mb-4">
                            <div className="form-label required">Facilities</div>
                            {isOrienteeLoading || isFacilitiesLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <>
                                    <Dropdown
                                        className="w-100 p-dropdown-tall"
                                        panelClassName="show-10"
                                        showClear={true}
                                        name="facility"
                                        value={orientee.facility_id}
                                        options={buildFacilityOptions(facilities)}
                                        filter={false}
                                        onChange={(evt) => {
                                            changeValue("facility_id", evt.target.value)
                                            changeValue("unit_id", null)
                                            changeValue("preceptors", [])
                                        }}
                                        disabled={!editable}
                                    />
                                    <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
                                        {errorFacility}
                                    </div>
                                </>
                            )}
                        </div>
                        {orientee.facility_id && (
                            <div className="col-lg-6 mb-4">
                                <div className="form-label required">Unit</div>
                                {isOrienteeLoading || isUnitsLoading ? (
                                    <Skeleton className="w-100 h-40px" />
                                ) : (
                                    <>
                                        <Dropdown
                                            className="w-100 p-dropdown-tall"
                                            panelClassName="show-10"
                                            showClear={true}
                                            name="unit"
                                            value={orientee.unit_id}
                                            options={units.map((unit) => ({label: unit.name, value: unit.id}))}
                                            filter={false}
                                            onChange={(evt) => {
                                                changeValue("unit_id", evt.target.value)
                                                changeValue("preceptors", [])
                                            }}
                                            disabled={!editable}
                                        />
                                        <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
                                            {errorUnit}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-4 mb-4">
                        <div className="col-lg-6 mb-4">
                            <div className="form-label">Preceptors</div>
                            {isOrienteeLoading || isPreceptorsLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <MultiSelect
                                    showClear
                                    options={preceptors.map((preceptor) => {
                                        return {
                                            label: preceptor.first_name + " " + preceptor.last_name,
                                            value: preceptor.id,
                                        }
                                    })}
                                    value={
                                        orientee.preceptors &&
                                        validPreceptorIds &&
                                        orientee.preceptors
                                            .map((p) => p.preceptor_user_id)
                                            .filter((id) => id != null && validPreceptorIds.has(id))
                                    }
                                    className="w-100 p-dropdown-tall"
                                    placeholder="Preceptors"
                                    filter
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(option) => changeValue("preceptors", option.value)}
                                    // this is currently not editable by the api
                                    disabled={editable}
                                />
                            )}
                        </div>
                        <div className="col-lg-6 mb-4">
                            <div className="form-label">Employee FTE</div>
                            {isOrienteeLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <Form.Control
                                    name="employee_fte"
                                    type="number"
                                    required
                                    value={orientee.employee_fte || ""}
                                    onChange={(e) => changeValue("employee_fte", e.target.value)}
                                    disabled={!editable}
                                />
                            )}
                        </div>
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-4 mb-4">
                        <div className="col-lg-6 mb-4">
                            <div className="form-label">Position</div>
                            {isOrienteeLoading || isPositionsLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <Dropdown
                                    className="w-100 p-dropdown-tall"
                                    panelClassName="show-10"
                                    showClear={true}
                                    name="position"
                                    value={orientee.position_id}
                                    options={positions.map((pos) => {
                                        return {label: pos.name, value: pos.id}
                                    })}
                                    filter={false}
                                    onChange={(evt) => changeValue("position_id", evt.target.value)}
                                    disabled={!editable}
                                />
                            )}
                        </div>
                        <div className="col-lg-6 mb-4">
                            <div className="form-label">Prior Experience</div>
                            {isOrienteeLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <Dropdown
                                    className="w-100 p-dropdown-tall"
                                    options={experienceLevels.map((exp) => {
                                        return {label: exp.name, value: exp.id}
                                    })}
                                    filter={false}
                                    value={orientee.experience_level_id}
                                    showClear
                                    onChange={(option) => changeValue("experience_level_id", option.value)}
                                    disabled={!editable}
                                />
                            )}
                        </div>
                    </div>
                    <div className="d-flex flex-column flex-lg-row gap-4 mb-4">
                        <div className="col-lg-6 mb-4">
                            <div className="form-label">Nursing Program Completed</div>
                            {isOrienteeLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <Dropdown
                                    className="w-100 p-dropdown-tall"
                                    options={programs}
                                    filter={false}
                                    value={orientee.nursing_program_completed}
                                    showClear
                                    onChange={(option) => changeValue("nursing_program_completed", option.value)}
                                    disabled={!editable}
                                />
                            )}
                        </div>
                        <div className="col-lg-6 mb-4">
                            <div className="form-label">Nurse Graduate Board Date</div>
                            {isOrienteeLoading ? (
                                <Skeleton className="w-100 h-40px" />
                            ) : (
                                <Calendar
                                    showIcon
                                    className="w-100 p-dropdown-tall"
                                    baseZIndex={1500}
                                    placeholder="Nurse Graduate Board Date"
                                    value={getJSDate(orientee.nurse_graduate_board_date)}
                                    onChange={(e) => changeValue("nurse_graduate_board_date", e.value)}
                                    disabled={!editable}
                                />
                            )}
                        </div>
                    </div>
                    <div className={cx("mb-6", {"d-none": !editable})}>
                        {isOrienteeLoading || isUnitsLoading ? (
                            <Skeleton className="w-100 h-40px" />
                        ) : (
                            <>
                                <Button
                                    variant="primary"
                                    disabled={!isOrienteeEdited}
                                    onClick={saveOrientee}
                                    className="me-4"
                                >
                                    {saving ? (
                                        <>
                                            <span className="me-2">Please wait...</span>
                                            <Spinner as="span" animation="border" size="sm" aria-hidden="true" />
                                        </>
                                    ) : (
                                        <span className="indicator-label">Save</span>
                                    )}
                                </Button>
                                {/* <OrienteeTransferModal
                                    orientee={orientee}
                                    organizations={organizations}
                                    units={units}
                                /> */}
                            </>
                        )}
                    </div>
                </div>
            </div>
            <OrienteeOrientations orientee_id={orientee.id} user_id={orientee.user_id} />
        </>
    )
}

export default OrienteeDetails
